/* eslint-disable no-console */
import { useCallback, useEffect, useMemo } from 'react';

import { useCacheQuery } from '../../../hooks/use-cache-query';
import {
  BaseConversationSignalRMessage,
  BaseSignalRMessage,
  SignalRFilterKey,
} from '../../../hooks/use-signalr';
import { useAppContext } from '../../context/app-context';
import {
  ConversationItemDataFragment,
  GetConversationItemDocument,
  PushType,
} from '../../types/api/graphql';

export function useConversationItem(id: string | null | undefined) {
  const {
    data: conversationItemData,
    loading: conversationItemLoading,
    refetch,
  } = useCacheQuery(GetConversationItemDocument, {
    variables: {
      id: id ?? '',
    },
    skip: !id,
  });

  const { signalRMessage, addFilter } = useAppContext();

  const filter = useCallback(
    (message: BaseSignalRMessage) => {
      if (message && [PushType.ConversationUpdated].includes(message.type)) {
        const signalRMessageData = message as BaseConversationSignalRMessage;
        if (signalRMessageData.conversationId === id) {
          return true;
        }
      }
      return false;
    },
    [id]
  );

  useEffect(() => {
    if (id) {
      addFilter({ key: SignalRFilterKey.CONVERSATION, filter });
    }
  }, [addFilter, filter, id]);

  useEffect(() => {
    if (signalRMessage && filter(signalRMessage)) {
      console.log(`[${SignalRFilterKey.CONVERSATION}] refetch`);
      refetch();
    }
  }, [filter, refetch, signalRMessage]);

  const data = useMemo(
    () =>
      (conversationItemData?.conversation as ConversationItemDataFragment) ??
      undefined,
    [conversationItemData?.conversation]
  );

  return {
    data,
    loading: conversationItemLoading,
  };
}
